'use client';

import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { postWebPushSubscription } from '@module/mdrt-org/shared/utils/data/post-web-push-subscription';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { buildWebPushSubscriptionPayload, createPushSubscription, isPushSupported } from '@module/mdrt-org/shared/utils/helpers/web-push-subscription';
import { getAppleDeviceInfo } from '@shared/utils/helpers/get-apple-device-info';
import { useInstallPromptCoordination } from '../providers/install-prompt-coordination-context';
import { useUserContext } from '../providers/user-context';
import { isPushPromptShownCookieSet, setPushPromptShownCookie } from './push-prompt-shown-cookie';
const isStandalone = (): boolean =>
// @ts-expect-error - navigator.standalone is iOS Safari only
Boolean(window.navigator.standalone) || window.matchMedia('(display-mode: standalone)').matches;
const shouldShowPrePrompt = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isPushPromptShownCookieSet()) return false;
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'default') return false;

  // iOS (iPhone/iPad) only supports web push when the site is installed as a PWA
  // (standalone). Desktop macOS Safari supports web push in a normal tab, so the
  // standalone gate must not apply there.
  const {
    isIPad,
    isIPhone
  } = getAppleDeviceInfo();
  const isIos = isIPhone || isIPad;
  if (isIos && !isStandalone()) return false;
  return true;
};
const MODAL_OPEN_DELAY_MS = 2000;
const delay = (ms: number): Promise<void> => new Promise(resolve => {
  setTimeout(resolve, ms);
});

// Waits until BOTH the service worker is active AND a short organic delay has elapsed before
// the modal opens. Awaiting `navigator.serviceWorker.ready` (with no upper timeout) is the
// fix for the macOS Safari bug where the SW wasn't ready when the one-shot registration check
// ran, so the modal never appeared — here we wait as long as Safari needs. The concurrent
// delay only enforces a minimum wait so the modal doesn't flash in on the very first render.
// If the SW never becomes active (e.g. development, where Serwist is disabled) the modal does
// not open, which is intended.
const waitBeforeShowing = async (): Promise<void> => {
  await Promise.all([navigator.serviceWorker.ready, delay(MODAL_OPEN_DELAY_MS)]);
};
export const usePushNotificationPrePrompt = () => {
  const {
    accessToken,
    authenticatedPersonId,
    isAuthenticated
  } = useUserContext();
  const {
    isPwaPromptActive,
    setIsPushPromptActive
  } = useInstallPromptCoordination();
  const [isOpen, setIsOpen] = useState(false);
  const [isReadyToOpen, setIsReadyToOpen] = useState(false);
  const [isHandlingPushAllow, setIsHandlingPushAllow] = useState(false);
  const hasShownRef = useRef(false);
  const {
    mutate
  } = useMutation({
    mutationFn: postWebPushSubscription,
    mutationKey: [AccountSettingsMutationKey.PATCH_PUSH_NOTIFICATION_SUBSCRIPTION]
  });
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (!shouldShowPrePrompt()) return undefined;
    let isActive = true;

    // Wait until the service worker is active (with a short organic delay) before flagging the
    // modal as ready to open, so it doesn't appear on the very first render and — on macOS
    // Safari — doesn't get skipped because the SW wasn't ready yet. `waitBeforeShowing` awaits
    // `navigator.serviceWorker.ready` with no upper timeout, so the modal will not open if the
    // SW never becomes active (e.g. development).
    const prepareToOpen = async () => {
      await waitBeforeShowing();
      if (!isActive) return;
      setIsReadyToOpen(true);
    };
    prepareToOpen();
    return () => {
      isActive = false;
    };
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isReadyToOpen) return;
    if (hasShownRef.current) return;
    if (isPwaPromptActive) return;
    hasShownRef.current = true;
    setIsOpen(true);
    setIsPushPromptActive(true);
    setPushPromptShownCookie();
  }, [isReadyToOpen, isPwaPromptActive, setIsPushPromptActive]);
  const closeModal = () => {
    setIsOpen(false);
    setIsPushPromptActive(false);
  };
  const onAllow = async () => {
    setIsHandlingPushAllow(true);
    try {
      const subscription = await createPushSubscription();
      if (!subscription) {
        // native prompt was dismissed without a choice — close modal, leave subscription off
        closeModal();
        return;
      }
      closeModal();
      mutate(buildWebPushSubscriptionPayload(subscription, {
        accessToken,
        personId: authenticatedPersonId
      }));
    } catch {
      // push setup failed (e.g. service worker unavailable) — close modal, leave subscription off
      closeModal();
    } finally {
      setIsHandlingPushAllow(false);
    }
  };
  const onClose = () => closeModal();
  return {
    isLoading: isHandlingPushAllow,
    onAllow,
    onClose,
    shouldShow: isOpen
  };
};